import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { useNetwork } from '@hooks/useNetwork'
import { useForumTopicCreate } from '@services/api/graphql/mutations/forumTopicCreate'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Text, TextArea, XStack, YStack, isWeb } from 'tamagui'

type ToastType = 'success' | 'error'

interface ITopicFormProps {
  courseContentId: string
  onCreated?: () => void
  onToast?: (type: ToastType, message: string) => void
  onCancel?: () => void
}

export function TopicForm({
  courseContentId,
  onCreated,
  onToast,
  onCancel,
}: ITopicFormProps) {
  const { t } = useTranslation('Comments')
  const { isConnected } = useNetwork()
  const { handleCreateTopic, loading } = useForumTopicCreate()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | undefined>()
  // Guarda de reentrância síncrona: o `loading` do useMutation só vira true no
  // próximo render, então um mesmo clique que dispare onPress duas vezes no
  // mesmo tick criaria dois tópicos (duplicação). O ref bloqueia a 2ª chamada
  // imediatamente, antes de qualquer await.
  const isSubmittingRef = useRef(false)

  const isSubmitDisabled = loading || !title.trim() || !description.trim()

  const onSubmit = async () => {
    if (isSubmittingRef.current) return

    const trimmedTitle = title.trim()
    const trimmedDescription = description.trim()

    if (!trimmedTitle) {
      setError(t('newTopic.validations.titleRequired'))
      return
    }

    if (!trimmedDescription) {
      setError(t('newTopic.validations.descriptionRequired'))
      return
    }

    if (!isConnected) {
      onToast?.('error', t('newTopic.offline'))
      return
    }

    isSubmittingRef.current = true
    try {
      const result = await handleCreateTopic({
        courseContentId,
        title: trimmedTitle,
        description: trimmedDescription,
      })

      if (result?.errors && result.errors.length > 0) {
        onToast?.('error', result.errors[0])
        return
      }

      setTitle('')
      setDescription('')
      setError(undefined)
      onCreated?.()
      onToast?.('success', t('newTopic.success'))
    } catch {
      onToast?.('error', t('newTopic.error'))
    } finally {
      isSubmittingRef.current = false
    }
  }

  // Web: comentário de campo único ("Seu comentário"), conforme o design.
  // O título do tópico recebe o próprio texto (description fica vazia).
  const onSubmitWeb = async () => {
    if (isSubmittingRef.current) return

    const text = description.trim()
    if (!text) {
      setError(t('newTopic.validations.descriptionRequired'))
      return
    }
    if (!isConnected) {
      onToast?.('error', t('newTopic.offline'))
      return
    }
    isSubmittingRef.current = true
    try {
      const result = await handleCreateTopic({
        courseContentId,
        title: text,
        description: '',
      })
      if (result?.errors && result.errors.length > 0) {
        onToast?.('error', result.errors[0])
        return
      }
      setTitle('')
      setDescription('')
      setError(undefined)
      onCreated?.()
      onToast?.('success', t('newTopic.success'))
    } catch {
      onToast?.('error', t('newTopic.error'))
    } finally {
      isSubmittingRef.current = false
    }
  }

  if (isWeb) {
    return (
      <YStack gap="$spacing-inset-xs" testID="forum-topic-form">
        <TextArea
          value={description}
          onChangeText={(value) => {
            setDescription(value)
            if (error) setError(undefined)
          }}
          placeholder={t('newTopic.placeholder')}
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$text-primary"
          backgroundColor="$bg-input"
          placeholderTextColor="$text-placeholder"
          borderColor={error ? '$danger-300' : '$border-default'}
          minHeight={96}
          testID="forum-topic-form-description"
        />
        <XStack justifyContent="flex-end" gap="$spacing-inset-xs">
          {onCancel && (
            <Button
              variant="secondary"
              size="small"
              onPress={() => {
                setDescription('')
                setError(undefined)
                onCancel()
              }}
              testID="forum-topic-form-cancel"
            >
              {t('newTopic.cancel')}
            </Button>
          )}
          <Button
            variant="primary"
            size="small"
            onPress={onSubmitWeb}
            disabled={loading || !description.trim()}
            loading={loading}
            testID="forum-topic-form-submit"
          >
            {t('newTopic.submit')}
          </Button>
        </XStack>
        {error && (
          <Text
            fontFamily="$mulishRegular"
            fontSize="$font-size-xxs"
            color="$danger-300"
          >
            {error}
          </Text>
        )}
      </YStack>
    )
  }

  return (
    <YStack gap="$spacing-inset-xs" testID="forum-topic-form">
      <Input
        value={title}
        onChangeText={(value) => {
          setTitle(value)
          if (error) setError(undefined)
        }}
        placeholder={t('newTopic.titlePlaceholder')}
        fontFamily="$mulishRegular"
        fontSize="$font-size-xxs"
        color="$text-primary"
        backgroundColor="$bg-input"
        placeholderTextColor="$text-placeholder"
        borderColor="$border-default"
        height={44}
        paddingHorizontal="$spacing-inset-xs"
        testID="forum-topic-form-title"
      />
      <TextArea
        value={description}
        onChangeText={(value) => {
          setDescription(value)
          if (error) setError(undefined)
        }}
        placeholder={t('newTopic.descriptionPlaceholder')}
        fontFamily="$mulishRegular"
        fontSize="$font-size-xxs"
        color="$text-primary"
        backgroundColor="$bg-input"
        placeholderTextColor="$text-placeholder"
        borderColor="$border-default"
        minHeight={80}
        numberOfLines={4}
        testID="forum-topic-form-description"
      />
      <Button
        variant="primary"
        onPress={onSubmit}
        disabled={isSubmitDisabled}
        loading={loading}
        testID="forum-topic-form-submit"
        alignSelf="stretch"
        width="100%"
      >
        <XStack
          flex={1}
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <HeroIcon icon={'paper-plane'} size={18} color="$text-on-accent" />
        </XStack>
      </Button>
      {error && (
        <Text
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$danger-300"
        >
          {error}
        </Text>
      )}
    </YStack>
  )
}
